/**
 * Input con etiqueta flotante “outlined” (muesca sobre el borde).
 *
 * QUÉ HACE:
 * - Coloca la etiqueta dentro del campo; al enfocar o si hay valor, la anima al borde superior
 *   con fondo que corta la línea del borde (efecto -----Etiqueta-----).
 * - Acepta icono izquierdo, trailingSlot centrado a la derecha (ej. ojo mostrar contraseña), endAdornment arriba a la derecha, y hint.
 * - Por defecto relleno + muesca = misma superficie (blanco en claro, alineado a tarjetas auth); notchBgClassName solo para fondos distintos.
 *
 * QUÉ EXPORTA: FloatingLabelInput, FloatingLabelTextarea
 *
 * QUÉ NO DEBE HACER:
 * - NO contiene lógica de negocio ni llamadas a API.
 *
 * DEPENDE DE: React, utilidades de tema Tailwind (primary, gray/slate).
 */

import {
  forwardRef,
  InputHTMLAttributes,
  TextareaHTMLAttributes,
  ReactNode,
  useId,
  useState,
} from "react";

/** Superficie por defecto: blanco puro en claro (misma lectura que la tarjeta); slate en oscuro. */
const DEFAULT_FIELD_SURFACE = "bg-white dark:bg-slate-800";

export interface FloatingLabelInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "placeholder"
> {
  /** Texto de la etiqueta (también accesible vía htmlFor). */
  label: string;
  /** Placeholder visible solo cuando la etiqueta está flotando (en foco o con valor). */
  hint?: string;
  icon?: ReactNode;
  /** Centrado en vertical a la derecha (p. ej. botón mostrar/ocultar contraseña). */
  trailingSlot?: ReactNode;
  /** Contenido en la esquina superior derecha (p. ej. “¿Olvidaste tu contraseña?”). */
  endAdornment?: ReactNode;
  error?: string;
  /** Fondo del interior del campo; por defecto blanco / slate acorde a tarjetas. */
  fieldBgClassName?: string;
  /** Si no se pasa, la muesca usa el mismo fondo que el campo (evita halo distinto). */
  notchBgClassName?: string;
  containerClassName?: string;
  /**
   * Reemplaza las clases de borde por defecto (primary o error).
   * Útil para estados de validación no cubiertos por `error` (p. ej. advertencia o éxito).
   */
  borderClassName?: string;
}

export const FloatingLabelInput = forwardRef<
  HTMLInputElement,
  FloatingLabelInputProps
>(
  (
    {
      label,
      hint,
      icon,
      trailingSlot,
      endAdornment,
      error,
      fieldBgClassName = DEFAULT_FIELD_SURFACE,
      notchBgClassName,
      containerClassName = "",
      borderClassName,
      className = "",
      disabled,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      id: idProp,
      ...rest
    },
    ref,
  ) => {
    const notchSurfaceClass = notchBgClassName ?? fieldBgClassName;
    const uid = useId();
    const inputId = idProp ?? `floating-label-${uid}`;
    const [focused, setFocused] = useState(false);
    const [uncontrolledSnapshot, setUncontrolledSnapshot] = useState(() =>
      String(defaultValue ?? ""),
    );

    const isControlled = value !== undefined;
    const current = isControlled ? String(value ?? "") : uncontrolledSnapshot;
    const floated = focused || current.length > 0;

    const borderTone =
      borderClassName ??
      (error
        ? "border-red-300 dark:border-red-600 focus-within:border-red-500 dark:focus-within:border-red-400"
        : "border-primary-500/80 dark:border-primary-400/80 hover:border-primary-600 dark:hover:border-primary-300 focus-within:border-primary-600 dark:focus-within:border-primary-300");

    const labelLeft = icon ? "left-9" : "left-3";
    const labelFloatedLeft = "left-2.5";

    const padRight = trailingSlot
      ? "pr-10"
      : endAdornment
        ? "pr-[min(42%,9.5rem)]"
        : "pr-3";

    return (
      <div className={`relative pt-1 ${containerClassName}`}>
        <div
          className={`
            floating-label-field relative overflow-visible rounded-lg border shadow-none ring-0
            focus-within:ring-0
            ${fieldBgClassName}
            transition-[border-color] duration-200 ease-out
            ${borderTone}
            ${disabled ? "opacity-50" : ""}
          `}
        >
          {icon ? (
            <span
              className={`z-[2] left-2.5 ${
                floated
                  ? "input-icon-container-floated"
                  : "input-icon-container"
              }`}
              aria-hidden
            >
              {icon}
            </span>
          ) : null}

          {endAdornment ? (
            <div className="absolute right-2 top-2 z-[6] max-w-[55%] text-right">
              {endAdornment}
            </div>
          ) : null}

          {trailingSlot ? (
            <div
              className={`z-[6] right-1.5 flex items-center [&_button]:shrink-0 ${
                floated
                  ? "input-icon-container-floated"
                  : "input-icon-container"
              }`}
            >
              {trailingSlot}
            </div>
          ) : null}

          <div className="floating-label-field-clip relative overflow-hidden rounded-lg">
            <input
              ref={ref}
              id={inputId}
              disabled={disabled}
              {...(isControlled ? { value } : { defaultValue })}
              onChange={(e) => {
                if (!isControlled) setUncontrolledSnapshot(e.target.value);
                onChange?.(e);
              }}
              onFocus={(e) => {
                setFocused(true);
                onFocus?.(e);
              }}
              onBlur={(e) => {
                setFocused(false);
                onBlur?.(e);
              }}
              placeholder={floated ? (hint ?? "") : "\u00a0"}
              className={`
                w-full rounded-lg bg-transparent text-xs text-gray-900 dark:text-slate-100
                placeholder:text-gray-400 dark:placeholder:text-slate-500
                outline-none ring-0 focus:ring-0
                ${icon ? "pl-9" : "pl-3"}
                ${padRight}
                ${floated ? "pt-3.5 pb-2" : "py-2.5"}
                disabled:cursor-not-allowed
                ${className}
              `}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? `${inputId}-error` : undefined}
              {...rest}
            />
          </div>

          <label
            htmlFor={inputId}
            className={`
              pointer-events-none absolute z-[5] select-none transition-all duration-200 ease-out
              ${
                floated
                  ? `${labelFloatedLeft} top-0 -translate-y-1/2 text-[9px] font-bold uppercase tracking-wider text-primary-600 dark:text-primary-400 leading-none`
                  : `${labelLeft} abs-center-y text-[11px] font-medium text-gray-500 dark:text-slate-400 leading-snug`
              }
            `}
          >
            {floated ? (
              <span
                className={`${notchSurfaceClass} inline-block rounded-sm px-1.5 py-0.5 shadow-[0_0_0_2px_transparent]`}
              >
                {label}
              </span>
            ) : (
              label
            )}
          </label>
        </div>
        {error ? (
          <p
            id={`${inputId}-error`}
            className="mt-1 text-[10px] font-medium text-red-600 dark:text-red-400"
            role="alert"
          >
            {error}
          </p>
        ) : null}
      </div>
    );
  },
);

FloatingLabelInput.displayName = "FloatingLabelInput";

export interface FloatingLabelTextareaProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  "placeholder"
> {
  label: string;
  hint?: string;
  error?: string;
  fieldBgClassName?: string;
  notchBgClassName?: string;
  containerClassName?: string;
}

export const FloatingLabelTextarea = forwardRef<
  HTMLTextAreaElement,
  FloatingLabelTextareaProps
>(
  (
    {
      label,
      hint,
      error,
      fieldBgClassName = DEFAULT_FIELD_SURFACE,
      notchBgClassName,
      containerClassName = "",
      className = "",
      disabled,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      id: idProp,
      rows = 3,
      ...rest
    },
    ref,
  ) => {
    const notchSurfaceClass = notchBgClassName ?? fieldBgClassName;
    const uid = useId();
    const taId = idProp ?? `floating-label-ta-${uid}`;
    const [focused, setFocused] = useState(false);
    const [uncontrolledSnapshot, setUncontrolledSnapshot] = useState(() =>
      String(defaultValue ?? ""),
    );

    const isControlled = value !== undefined;
    const current = isControlled ? String(value ?? "") : uncontrolledSnapshot;
    const floated = focused || current.length > 0;

    const borderTone = error
      ? "border-red-300 dark:border-red-600 focus-within:border-red-500 dark:focus-within:border-red-400"
      : "border-primary-500/80 dark:border-primary-400/80 hover:border-primary-600 dark:hover:border-primary-300 focus-within:border-primary-600 dark:focus-within:border-primary-300";

    const labelLeft = "left-3";
    const labelFloatedLeft = "left-2.5";

    return (
      <div className={`relative pt-1 ${containerClassName}`}>
        <div
          className={`
            floating-label-field relative overflow-visible rounded-lg border shadow-none ring-0
            focus-within:ring-0
            ${fieldBgClassName}
            transition-[border-color] duration-200 ease-out
            ${borderTone}
            ${disabled ? "opacity-50" : ""}
          `}
        >
          <div className="floating-label-field-clip relative overflow-hidden rounded-lg">
            <textarea
              ref={ref}
              id={taId}
              disabled={disabled}
              rows={rows}
              {...(isControlled ? { value } : { defaultValue })}
              onChange={(e) => {
                if (!isControlled) setUncontrolledSnapshot(e.target.value);
                onChange?.(e);
              }}
              onFocus={(e) => {
                setFocused(true);
                onFocus?.(e);
              }}
              onBlur={(e) => {
                setFocused(false);
                onBlur?.(e);
              }}
              placeholder={floated ? (hint ?? "") : "\u00a0"}
              className={`
                w-full resize-none rounded-lg bg-transparent text-sm text-gray-900 dark:text-slate-100
                placeholder:text-gray-400 dark:placeholder:text-slate-500
                outline-none ring-0 focus:ring-0
                px-3
                ${floated ? "pt-5 pb-2" : "py-3"}
                disabled:cursor-not-allowed
                ${className}
              `}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? `${taId}-error` : undefined}
              {...rest}
            />
          </div>

          <label
            htmlFor={taId}
            className={`
              pointer-events-none absolute z-[5] max-w-[calc(100%-1rem)] select-none transition-all duration-200 ease-out
              ${
                floated
                  ? `${labelFloatedLeft} top-0 -translate-y-1/2 text-[9px] font-bold uppercase tracking-wider text-primary-600 dark:text-primary-400 leading-none`
                  : `${labelLeft} abs-center-y text-[11px] font-medium text-gray-500 dark:text-slate-400 leading-snug`
              }
            `}
          >
            {floated ? (
              <span
                className={`${notchSurfaceClass} inline-block rounded-sm px-1.5 py-0.5`}
              >
                {label}
              </span>
            ) : (
              label
            )}
          </label>
        </div>
        {error ? (
          <p
            id={`${taId}-error`}
            className="mt-1 text-[10px] font-medium text-red-600 dark:text-red-400"
            role="alert"
          >
            {error}
          </p>
        ) : null}
      </div>
    );
  },
);

FloatingLabelTextarea.displayName = "FloatingLabelTextarea";
